import angularModule from 'Careers/angularModule/scripts/careers_module';
import template from 'Careers/angularModule/views/edit_career_profile/student_network_settings_form.html';
import cacheAngularTemplate from 'cacheAngularTemplate';
import clone from 'Utils/clone';
import deepEqual from 'Utils/deepEqual';
import { getCohort } from 'Users';
import { PERSONAL_FACT_MAX_CHARS, STUDENT_NETWORK_EMAIL_MAX_CHARS } from 'FormConfigs';

const templateUrl = cacheAngularTemplate(angularModule, template);

angularModule.directive('studentNetworkSettingsForm', [
    '$injector',

    function factory($injector) {
        const FormHelper = $injector.get('FormHelper');
        const EditCareerProfileHelper = $injector.get('EditCareerProfileHelper');
        const TranslationHelper = $injector.get('TranslationHelper');

        return {
            restrict: 'E',
            // see STEP_FORM_ATTRS in edit_career_profile_dir.js
            scope: {
                // form-step-validity reads this by name off the scope. See STEP_FORM_ATTRS.
                $setValidity: '<setValidity',
                careerProfile: '<',
                currentUser: '<',
                formContext: '<',
                transientFormValues: '<',
            },
            templateUrl,
            link(scope) {
                EditCareerProfileHelper.logEventForApplication(scope.formContext, 'viewed-student_network_settings');

                // This step only appears in the student profile, where the user has already been placed in a
                // cohort. edit_career_profile_dir.js used to put the cohort on the shared scope for us.
                scope.cohort = getCohort(scope.currentUser);

                scope.personalFactMaxChars = PERSONAL_FACT_MAX_CHARS;
                scope.studentNetworkEmailMaxChars = STUDENT_NETWORK_EMAIL_MAX_CHARS;

                FormHelper.supportForm(scope, scope.student_network_settings);

                // Publish the three user facts the schema needs for the preferred email. They are on
                // the user rather than on either form model, so the schema cannot reach them. See
                // transientFormValues in edit_career_profile_dir.js.
                //
                // The first two decide whether the address is required. The third is the other half
                // of the `ng-if` on the row, so the schema can apply the format rules only while the
                // input is on the page.
                scope.transientFormValues.has_access_to_full_student_network_profiles =
                    !!scope.currentUser.hasAccessToFullStudentNetworkProfiles;
                scope.transientFormValues.account_email_is_valid_for_student_network =
                    scope.currentUser.isEmailValidForStudentNetwork;
                scope.transientFormValues.account_has_student_network_email = !!scope.currentUser.student_network_email;

                // Publish the text in the preferred email input. Angular writes `undefined` to the model
                // for an address it rejects, so the schema judges the format from this text instead.
                //
                // The value is `undefined` while the row is off the page, because the input then has
                // no control on the form.
                scope.$watch(
                    () => scope.student_network_settings.student_network_email?.$viewValue,
                    viewValue => {
                        scope.transientFormValues.student_network_email_view_value = viewValue;
                    },
                );

                const translationHelper = new TranslationHelper(
                    'careers.edit_career_profile.student_network_settings_form',
                );

                scope.isNonEnglish = FormHelper.isNonEnglish();

                if (scope.currentUser.programType === 'career_network_only') {
                    scope.funFactDescKey = `careers.edit_career_profile.student_network_settings_form.fun_fact_desc${
                        scope.isNonEnglish ? '_non_english' : ''
                    }`;
                } else {
                    scope.funFactDescKey = `careers.edit_career_profile.student_network_settings_form.fun_fact_tell_us_classmates_only${
                        scope.isNonEnglish ? '_non_english' : ''
                    }`;
                }

                scope.placeholderFact = translationHelper.get('fun_fact_placeholder');

                Object.defineProperty(scope, 'studentNetworkEmailRequired', {
                    get() {
                        return (
                            scope.careerProfile.pref_student_network_privacy === 'full' &&
                            scope.currentUser.hasAccessToFullStudentNetworkProfiles &&
                            !scope.currentUser.isEmailValidForStudentNetwork
                        );
                    },
                });

                const initialInterests = clone(scope.careerProfile.student_network_interests);

                scope.$watch('careerProfile.student_network_interests.length', (newLength, oldLength) => {
                    // if we're resetting the careerProfile after the form was dirtied, we don't want to re-dirty
                    if (
                        oldLength !== newLength &&
                        !deepEqual(initialInterests, scope.careerProfile.student_network_interests)
                    ) {
                        scope.student_network_settings.$setDirty(true);
                    }
                });

                scope.lookingForOptions = $injector.get('STUDENT_NETWORK_LOOKING_FOR_KEYS');
                scope.privacyOptions = ['full', 'anonymous', 'hidden'];

                const propsValuesMap = {
                    student_network_looking_for: scope.lookingForOptions,
                };
                FormHelper.supportCheckboxGroups(scope, scope.careerProfile, propsValuesMap);
            },
        };
    },
]);
